import { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { categoriesAPI, productsAPI } from '../../lib/api';
import { useApiQuery } from '../../hooks/useApiQuery';
import { Tag, Package, Search } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

const AdminCategories = () => {
  const [search, setSearch] = useState('');

  const { data: categoriesRes, isLoading: loading } = useApiQuery(
    ['categories'],
    () => categoriesAPI.getAll(),
    {
      select: (res) => {
        const categoriesData = res.data.map(cat => ({
          name: cat.name,
          count: cat.count
        }));
        const stats = {};
        categoriesData.forEach(cat => {
          stats[cat.name] = cat.count;
        });
        return { categoriesData, stats };
      }
    }
  );

  const categories = categoriesRes?.categoriesData || [];
  const categoryStats = categoriesRes?.stats || {};

  const filteredCategories = categories.filter(cat => 
    cat.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-categories-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Categories
          </h1>
          <p className="text-slate-600">View all product categories ({categories.length} total)</p>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Categories are automatically created when you add products. 
            To add a new category, simply create a product with that category name.
          </p>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
          <Input
            placeholder="Search categories..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="category-search-input"
          />
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {loading ? (
            [...Array(8)].map((_, i) => (
              <div key={i} className="h-24 bg-slate-200 rounded-xl skeleton" />
            ))
          ) : filteredCategories.length === 0 ? (
            <div className="col-span-full text-center py-12 text-slate-500">
              <Tag className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              {search ? 'No categories match your search' : 'No categories found'}
            </div>
          ) : (
            filteredCategories.map((category) => (
              <div 
                key={category.name} 
                className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow"
                data-testid={`category-${category.name}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Tag className="w-5 h-5 text-[#8acb95]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm truncate" title={category.name}>
                      {category.name}
                    </h3>
                    <div className="flex items-center gap-1 mt-1">
                      <Package className="w-3 h-3 text-slate-400" />
                      <span className="text-xs text-slate-500">
                        {category.count} products
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Summary */}
        {!loading && (
          <div className="bg-slate-50 rounded-xl p-4 flex items-center justify-between">
            <span className="text-sm text-slate-600">
              Showing {filteredCategories.length} of {categories.length} categories
            </span>
            <Badge variant="outline" className="bg-white">
              {Object.values(categoryStats).reduce((a, b) => a + b, 0)} total products
            </Badge>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminCategories;
